import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { formatCurrency } from '../utils/units';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#FFFFFF'
  },
  header: {
    marginBottom: 20,
    borderBottom: '1 solid #B4A06C',
    paddingBottom: 10
  },
  title: {
    fontSize: 24,
    color: '#8B7355',
    marginBottom: 10
  },
  subtitle: {
    fontSize: 14,
    color: '#666666',
    marginBottom: 5
  },
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 16,
    color: '#8B7355',
    marginBottom: 10
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    flex: 1,
    color: '#666666'
  },
  value: {
    flex: 2,
    color: '#333333'
  },
  materialImage: {
    width: 150,
    height: 100,
    objectFit: 'cover',
    marginBottom: 10
  },
  priceSection: {
    marginTop: 20,
    borderTop: '1 solid #E5DED3',
    paddingTop: 10
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5
  },
  total: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
    borderTop: '1 solid #B4A06C',
    paddingTop: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#8B7355'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    color: '#666666',
    fontSize: 10,
    borderTop: '1 solid #E5DED3',
    paddingTop: 10
  }
});

interface OrderPDFProps {
  orderDetails: {
    customerInfo: {
      name: string;
      email: string;
      phone?: string;
      company?: string;
    };
    projectDetails: {
      type: string;
      timeline: string;
      description: string;
    };
    materials: Array<{
      name: string;
      image: string;
      quantity: number;
      area: number;
      price: number;
    }>;
    services: Array<{
      name: string;
      price: number;
    }>;
    totals: {
      subtotal: number;
      vat: number;
      total: number;
    };
  };
}

export const OrderPDF: React.FC<OrderPDFProps> = ({ orderDetails }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Order Details</Text>
        <Text style={styles.subtitle}>Reference: {new Date().toISOString().split('T')[0]}-{Math.random().toString(36).substr(2, 9)}</Text>
      </View>

      {/* Customer Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Customer Information</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Name:</Text>
          <Text style={styles.value}>{orderDetails.customerInfo.name}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Email:</Text>
          <Text style={styles.value}>{orderDetails.customerInfo.email}</Text>
        </View>
        {orderDetails.customerInfo.phone && (
          <View style={styles.row}>
            <Text style={styles.label}>Phone:</Text>
            <Text style={styles.value}>{orderDetails.customerInfo.phone}</Text>
          </View>
        )}
        {orderDetails.customerInfo.company && (
          <View style={styles.row}>
            <Text style={styles.label}>Company:</Text>
            <Text style={styles.value}>{orderDetails.customerInfo.company}</Text>
          </View>
        )}
      </View>

      {/* Project Details */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Project Details</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Type:</Text>
          <Text style={styles.value}>{orderDetails.projectDetails.type}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Timeline:</Text>
          <Text style={styles.value}>{orderDetails.projectDetails.timeline}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Description:</Text>
          <Text style={styles.value}>{orderDetails.projectDetails.description}</Text>
        </View>
      </View>

      {/* Materials */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Selected Materials</Text>
        {orderDetails.materials.map((material, index) => (
          <View key={index} style={{ marginBottom: 15 }}>
            <Image src={material.image} style={styles.materialImage} />
            <View style={styles.row}>
              <Text style={styles.label}>Material:</Text>
              <Text style={styles.value}>{material.name}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Quantity:</Text>
              <Text style={styles.value}>{material.quantity} slabs</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Area:</Text>
              <Text style={styles.value}>{material.area} m²</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Price:</Text>
              <Text style={styles.value}>{formatCurrency(material.price * material.area, 'EUR', 'en')}</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Services */}
      {orderDetails.services.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Selected Services</Text>
          {orderDetails.services.map((service, index) => (
            <View key={index} style={styles.row}>
              <Text style={styles.label}>{service.name}:</Text>
              <Text style={styles.value}>{formatCurrency(service.price, 'EUR', 'en')}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Price Summary */}
      <View style={styles.priceSection}>
        <View style={styles.priceRow}>
          <Text>Subtotal:</Text>
          <Text>{formatCurrency(orderDetails.totals.subtotal, 'EUR', 'en')}</Text>
        </View>
        <View style={styles.priceRow}>
          <Text>VAT (21%):</Text>
          <Text>{formatCurrency(orderDetails.totals.vat, 'EUR', 'en')}</Text>
        </View>
        <View style={styles.total}>
          <Text>Total Price (incl. VAT):</Text>
          <Text>{formatCurrency(orderDetails.totals.total, 'EUR', 'en')}</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text>This is a quote and is valid for 30 days from the date of issue.</Text>
        <Text>For questions about your order, please contact us at +1 (800) 123-4567 or support@example.com</Text>
      </View>
    </Page>
  </Document>
);